// Turns a failed request, or a form that is missing required fields, into a message the person can act on.

const WORDS = { po: "PO", rfq: "RFQ", pi: "PI", gstin: "GSTIN", pan: "PAN", hsn: "HSN", id: "ID", url: "URL", swift: "SWIFT", ifsc: "IFSC", cin: "CIN", vat: "VAT" };

export const humanize = (key) =>
  String(key)
    .replace(/_id$/, "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => WORDS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

// DRF sends {field: ["message"]}, {field: [{nested}]} for line items, {detail: "..."} or {non_field_errors: [...]}
const flatten = (data, path = []) => {
  if (data == null) return [];
  if (typeof data === "string") return [{ path, text: data }];
  if (Array.isArray(data)) {
    return data.flatMap((item, i) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const lineNo = data.length > 1 ? [`line ${i + 1}`] : [];
        return flatten(item, [...path, ...lineNo]);
      }
      return flatten(item, path);
    });
  }
  if (typeof data === "object") return Object.entries(data).flatMap(([k, v]) => flatten(v, k === "detail" || k === "non_field_errors" ? path : [...path, k]));
  return [{ path, text: String(data) }];
};

const line = ({ path, text }) => {
  if (!path.length) return text;
  const label = path.map((p) => (/^line \d+$/.test(p) ? p : humanize(p))).join(" - ");
  const shown = text === "This field is required." || text === "This field may not be blank." || text === "This field may not be null."
    ? "is required"
    : text.replace(/^This field /, "");
  return shown === "is required" ? `${label} is required` : `${label}: ${shown}`;
};

// The reason a request failed, in plain words.
export function errorText(err, fallback = "Something went wrong. Please try again.") {
  const res = err?.response;
  if (!res) return err?.code === "ERR_NETWORK" || err?.message === "Network Error" ? "Cannot reach the server. Check your connection and try again." : fallback;
  const { status, data } = res;
  if (status === 403) return (data && typeof data === "object" && data.detail) || "You do not have permission to do this.";
  if (status === 404) return "This record no longer exists. Refresh the page.";
  if (status >= 500) return "The server could not complete this request. Please try again, and tell the administrator if it keeps happening.";
  if (typeof data === "string") return fallback;
  const lines = flatten(data).map(line);
  return lines.length ? [...new Set(lines)].slice(0, 12).join("\n") : fallback;
}

const cleanLabel = (t) => t.replace(/\(.*?\)/g, "").replace(/[*:]/g, "").replace(/\s+/g, " ").trim();

const labelForElement = (el) => {
  try {
    const own = el.labels && el.labels[0];
    if (own) return cleanLabel(own.textContent);
    for (let box = el.parentElement, up = 0; box && up < 3; box = box.parentElement, up++) {
      const lab = box.querySelector("label");
      if (lab && !lab.contains(el)) return cleanLabel(lab.textContent);
      if (lab && lab.contains(el)) return cleanLabel(lab.textContent);
    }
  } catch { /* use the field name instead */ }
  return el.getAttribute("placeholder") || humanize(el.name || el.id || "this field");
};

const labelOf = (name) => {
  try {
    const el = document.querySelector(`[name="${CSS.escape(name)}"]`);
    if (el) return labelForElement(el);
  } catch { /* fall back to the field name */ }
  return humanize(name);
};

// Second argument of react-hook-form's handleSubmit: runs when required fields are empty or a value is invalid
export function showInvalid(errors) {
  const names = Object.keys(errors || {});
  if (!names.length) return;
  const rows = names.slice(0, 12).map((n) => {
    const msg = errors[n]?.message;
    const label = labelOf(n);
    return !msg || msg === "Required" || /required/i.test(msg) ? `• ${label} is required` : `• ${label}: ${msg}`;
  });
  window.alert(`Please fill in the following before saving:\n${rows.join("\n")}`);
}

// Forms that use the browser's own `required` check show a small bubble that is easy to miss. Show one alert that lists
// every empty required field instead.
let invalidBatch = [];
export function installRequiredAlert() {
  document.addEventListener("invalid", (e) => {
    e.preventDefault();
    if (!invalidBatch.length) {
      setTimeout(() => {
        const names = [...new Set(invalidBatch.map(labelForElement))].filter(Boolean).slice(0, 12);
        invalidBatch[0]?.focus?.();
        invalidBatch = [];
        window.alert(`Please fill in the following before saving:\n${names.map((n) => `• ${n} is required`).join("\n")}`);
      }, 0);
    }
    invalidBatch.push(e.target);
  }, true);
}

const SKIP = /\/(auth|reports|bulk|pdf-templates|lookup)|dry_run/;
// Safety net for every request: a failed save, update or delete always tells the person why.
export function notifyRequestFailure(error) {
  const cfg = error?.config;
  const status = error?.response?.status;
  if (!cfg || error.alerted || cfg.silent || !status || status === 401) return;
  const method = (cfg.method || "get").toLowerCase();
  if (method === "get" || SKIP.test(cfg.url || "")) return;
  error.alerted = true;
  alertFailure(method === "delete" ? "delete" : method === "post" ? "create" : "update", error, true);
}

// Tells the person that saving, updating or deleting did not happen, and why. `err` is the axios error.
export const alertFailure = (action, err, force = false) => {
  if (err?.alerted && !force) return;
  if (err && typeof err === "object") err.alerted = true;
  const reason = errorText(err, "Please try again.");
  window.alert(action === "delete" ? `Could not delete.
${reason}` : `Could not ${action === "update" ? "update" : "save"}.
${reason}`);
};
